import { getJsonScriptData, isValidEmailAddress } from 'utils';

const MODAL_ID = 'destination_unavailable_modal';
const FEEDBACK_MODAL_ID = 'destination_unavailable_feedback_modal';


document.addEventListener('DOMContentLoaded', () => {
  let isMarketable = null;
  let destinationSlug = null;
  let destinationSlugs = null;

  try {
    isMarketable = getJsonScriptData('destination_is_marketable');
    destinationSlug = getJsonScriptData('destination_slug');
    destinationSlugs = [destinationSlug, ];
  } catch {
    // In the lead generator we won't have these JSON script tags.
    // destinationSlugs will be set on calling the window.openUnmarketableDestinationModal function 'manually'.
  }

  let modalPromise = null;

  function openModal(modal) {
    modal.classList.add('is-active');
    modal.setAttribute('aria-hidden', 'false');
  }

  // The modal markup isn't rendered into the page so crawlers don't index its
  // (hidden) content. It's fetched on demand the first time it's needed, then cached.
  function loadModal() {
    if (modalPromise) return modalPromise;

    let basePromise;
    const existingModal = document.getElementById(MODAL_ID);

    if (existingModal) {
      basePromise = Promise.resolve(existingModal);
    } else if (destinationSlug) {
      basePromise = fetch(`/ajax/unmarketable-destination-modal/${destinationSlug}/`)
        .then((response) => {
          if (!response.ok) throw new Error('Failed to load unmarketable destination modal');
          return response.text();
        })
        .then((html) => {
          document.body.insertAdjacentHTML('beforeend', html);
          const modal = document.getElementById(MODAL_ID);
          if (window.htmx && typeof window.htmx.process === 'function') {
            window.htmx.process(modal);
          }
          return modal;
        });
    } else {
      basePromise = Promise.reject(new Error('Unmarketable destination modal is not available'));
    }

    modalPromise = basePromise
      .then((modal) => {
        setupModal(modal);
        return modal;
      })
      .catch((error) => {
        modalPromise = null;
        throw error;
      });

    return modalPromise;
  }

  const ctas = document.querySelectorAll('.js-lead-cta');
  if (ctas.length && isMarketable === false) {
    // The user is very likely to click a CTA on this page, so start fetching
    // the modal now instead of waiting for the click.
    loadModal().catch((error) => console.error(error));

    ctas.forEach((cta) => {
      cta.addEventListener('click', (event) => {
        event.preventDefault();
        loadModal().then(openModal).catch((error) => console.error(error));
      });
    });
  }

  window.openUnmarketableDestinationModal = (slugs) => {
    destinationSlugs = slugs;
    loadModal().then(openModal).catch((error) => console.error(error));
  };

  function setupModal(modal) {
    const feedbackModal = document.getElementById(FEEDBACK_MODAL_ID);

    async function sendAvailabilityRequest(email, acceptedTerms, destination_slug) {
      const csrfInput = document.querySelector('[name=csrfmiddlewaretoken]');
      if (!csrfInput) throw new Error('CSRF token input not found');
      const csrfToken = csrfInput.value;

      const response = await fetch('/ajax/destination-availability-request/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({
          email,
          accepted_terms: acceptedTerms,
          destination_slug: destination_slug,
        }),
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      return response.json();
    }

    const closeMainModal = () => {
      modal.classList.remove('is-active');
      modal.setAttribute('aria-hidden', 'true');
    };

    modal.querySelectorAll('[data-close-modal]').forEach((el) => {
      el.addEventListener('click', () => {
        closeMainModal();
      });
    });

    const showFeedbackModal = ({ title, message, isError }) => {
      if (!feedbackModal) return;

      const titleElement = document.getElementById('unmarketable_destination_feedback_title');
      const messageElement = document.getElementById('unmarketable_destination_feedback_message');

      if (titleElement) {
        titleElement.textContent = title;
        titleElement.classList.toggle('has-text-danger', isError);
        titleElement.classList.toggle('has-text-success', !isError);
      }

      if (messageElement) {
        messageElement.textContent = message;
      }

      feedbackModal.classList.add('is-active');
      feedbackModal.setAttribute('aria-hidden', 'false');
    };

    if (feedbackModal) {
      feedbackModal.querySelectorAll('[data-close-feedback-modal]').forEach((el) => {
        el.addEventListener('click', () => {
          feedbackModal.classList.remove('is-active');
          feedbackModal.setAttribute('aria-hidden', 'true');
        });
      });
    }

    const emailInput = document.getElementById('unmarketable_destination_email');
    const termsCheckbox = document.getElementById('unmarketable_destination_terms');
    const submitButton = document.getElementById('unmarketable_destination_submit');
    const errorElement = document.getElementById('unmarketable_destination_error');
    let isSubmitting = false;

    if (submitButton && emailInput && termsCheckbox && errorElement) {
      submitButton.addEventListener('click', async () => {
        if (isSubmitting) return;

        const email = emailInput.value.trim();

        if (!email) {
          errorElement.textContent = gettext('Please enter your email address');
          return;
        }

        if (!isValidEmailAddress(email)) {
          errorElement.textContent = gettext('Please enter a valid email address');
          return;
        }

        if (!termsCheckbox.checked) {
          errorElement.textContent = gettext('You must accept the terms to continue');
          return;
        }

        errorElement.textContent = '';
        isSubmitting = true;
        submitButton.disabled = true;

        try {
          for (const destination_slug of destinationSlugs) {
            await sendAvailabilityRequest(email, termsCheckbox.checked, destination_slug);
          }
          closeMainModal();
          showFeedbackModal({
            title: gettext('Request sent'),
            message: gettext('We will notify you when this destination is available again.'),
            isError: false,
          });
        } catch (e) {
          errorElement.textContent = '';
          closeMainModal();
          showFeedbackModal({
            title: gettext('Request failed'),
            message: gettext('An error occurred while saving your email. Please try again.'),
            isError: true,
          });
          console.error(e);
        } finally {
          isSubmitting = false;
          submitButton.disabled = false;
        }
      });
    }
  }
});
